import React, { useState, useEffect } from "react";
import { User, Key, ArrowRight, Mail, Lock, ChevronLeft, Calendar, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";

export default function ForgotPassword() {
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [needsAdditionalVerification, setNeedsAdditionalVerification] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [admissionNumber, setAdmissionNumber] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.authForgotPassword, {
        username: username,
        method: "username",
      });
      
      if (response.data.hasEmail === false) {
        setNeedsAdditionalVerification(true);
        setUserRole(response.data.userRole);
        setStep(1.5);
      } else {
        setUserEmail(response.data.email || "");
        setStep(2);
        setResendTimer(60);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const formatDateToIndian = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  const convertIndianDateToISO = (indianDate) => {
    const parts = indianDate.split('/');
    if (parts.length !== 3) return indianDate;
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleDateChange = (value) => {
    const formatted = formatDateToIndian(value);
    if (formatted.length <= 10) {
      setDateOfBirth(formatted);
    }
  };

  const handleVerifyAdditionalDetails = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const isoDate = convertIndianDateToISO(dateOfBirth);
      
      const payload = {
        username: username,
        email: verificationEmail,
        dateOfBirth: isoDate,
      };

      if (userRole === "student") {
        payload.admissionNumber = admissionNumber;
      } else if (userRole === "teacher") {
        payload.employeeCode = employeeCode;
      }

      const response = await axiosInstance.post(
        API_ENDPOINTS.authVerifyAdditionalDetails,
        payload
      );

      setUserEmail(response.data.email || verificationEmail);
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Verification failed. Please check your details and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setError("");
    setLoading(true);
    try {
      if (needsAdditionalVerification) {
        const isoDate = convertIndianDateToISO(dateOfBirth);
        const payload = {
          username: username,
          email: verificationEmail,
          dateOfBirth: isoDate,
        };

        if (userRole === "student") {
          payload.admissionNumber = admissionNumber;
        } else if (userRole === "teacher") {
          payload.employeeCode = employeeCode;
        }

        const response = await axiosInstance.post(
          API_ENDPOINTS.authVerifyAdditionalDetails,
          payload
        );
        setUserEmail(response.data.email || verificationEmail);
      } else {
        const response = await axiosInstance.post(API_ENDPOINTS.authForgotPassword, {
          username: username,
          method: "username",
        });
        setUserEmail(response.data.email || "");
      }
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.authVerifyOtp,
        {
          username: username,
          otp,
          method: "username",
        }
      );
      setStep(3);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to verify OTP"
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axiosInstance.post(API_ENDPOINTS.authResetPassword, {
        username: username,
        otp,
        newPassword,
        method: "username",
      });
      // Redirect to login or show success message
      window.location.href = "/login";
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step === 1.5) {
      setStep(1);
      setNeedsAdditionalVerification(false);
      setVerificationEmail("");
      setAdmissionNumber("");
      setEmployeeCode("");
      setDateOfBirth("");
    } else {
      setStep(prev => Math.max(1, prev - 1));
    }
    setError("");
  };

  const progressPercentage = (step === 1.5 ? 1.5 : step) / 3 * 100;
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-blue-50 p-4">
      <div className="max-w-md w-full">
        {/* Logo and School Name */}
        <div className="text-center mb-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
              <Lock className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="mt-4 text-2xl font-bold text-blue-800">Clynox</h1>
          <p className="text-blue-600">Account Recovery</p>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-in-out" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>

        <Card className="border-0 shadow-xl rounded-xl overflow-hidden">
          <div className="h-2 bg-blue-600 w-full"></div>
          <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-600 text-white">
            <CardTitle className="text-xl">
              {step === 1 && "Reset Your Password"}
              {step === 1.5 && "Additional Verification Required"}
              {step === 2 && "Verification Required"}
              {step === 3 && "Create New Password"}
            </CardTitle>
            <CardDescription className="text-blue-100">
              {step === 1 && "Enter your username to recover your account"}
              {step === 1.5 && userRole === "student" && "Please provide your email, admission number, and date of birth"}
              {step === 1.5 && userRole === "teacher" && "Please provide your email, employee ID, and date of birth"}
              {step === 2 && "Enter the verification code sent to your email"}
              {step === 3 && "Choose a strong, secure password"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {error && (
              <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 border border-red-200 rounded-lg flex items-center">
                <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                {error}
              </div>
            )}
            
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-gray-700 font-medium">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="username"
                      placeholder="Enter your username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleSubmitStep1}
                  className="w-full py-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200" 
                  disabled={loading || !username}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}

            {step === 1.5 && (
              <form onSubmit={handleVerifyAdditionalDetails} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verificationEmail" className="text-gray-700 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="verificationEmail"
                      placeholder="Enter your email address"
                      type="email"
                      value={verificationEmail}
                      onChange={(e) => setVerificationEmail(e.target.value)}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {userRole === "student" && (
                  <div className="space-y-2">
                    <Label htmlFor="admissionNumber" className="text-gray-700 font-medium">Admission Number</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                      <Input
                        id="admissionNumber"
                        placeholder="Enter your admission number"
                        type="text"
                        value={admissionNumber}
                        onChange={(e) => setAdmissionNumber(e.target.value)}
                        className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                )}

                {userRole === "teacher" && (
                  <div className="space-y-2">
                    <Label htmlFor="employeeCode" className="text-gray-700 font-medium">Employee ID</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                      <Input
                        id="employeeCode"
                        placeholder="Enter your employee ID"
                        type="text"
                        value={employeeCode}
                        onChange={(e) => setEmployeeCode(e.target.value)}
                        className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth" className="text-gray-700 font-medium">Date of Birth</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="dateOfBirth"
                      placeholder="DD/MM/YYYY"
                      type="text"
                      value={dateOfBirth}
                      onChange={(e) => handleDateChange(e.target.value)}
                      maxLength={10}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    Format: DD/MM/YYYY (e.g., 15/01/2000)
                  </p>
                </div>

                <Button 
                  type="submit"
                  className="w-full py-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200" 
                  disabled={loading || !verificationEmail || !dateOfBirth || (userRole === "student" && !admissionNumber) || (userRole === "teacher" && !employeeCode)}
                >
                  {loading ? "Verifying..." : "Verify & Send Code"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-gray-700 font-medium">Verification Code</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="otp"
                      placeholder="Enter verification code"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    Please check your email inbox for the verification code
                  </p>
                  {userEmail && (
                    <p className="text-xs text-blue-600 font-medium">
                      OTP sent to: {userEmail}
                    </p>
                  )}
                  <div className="flex justify-between items-center mt-2">
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0 || loading}
                      className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                      {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                    </button>
                  </div>
                </div>
                <Button 
                  onClick={handleVerifyOtp}
                  className="w-full py-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200" 
                  disabled={loading || !otp}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-700 font-medium">New Password</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="newPassword"
                      placeholder="New Password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">Confirm Password</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-5 w-5 text-blue-600" />
                    <Input
                      id="confirmPassword"
                      placeholder="Confirm New Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 py-6 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    <p>Password should include:</p>
                    <ul className="list-disc pl-5 mt-1">
                      <li>At least 8 characters</li>
                      <li>Numbers and letters</li>
                      <li>Special characters</li>
                    </ul>
                  </div>
                </div>
                <Button 
                  onClick={handleResetPassword}
                  className="w-full py-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200" 
                  disabled={loading || !newPassword || !confirmPassword}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-between border-t bg-gray-50 py-4 px-6">
            {(step > 1 || step === 1.5) && (
              <Button 
                variant="ghost" 
                onClick={goBack} 
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <div className="ml-auto">
              <Button 
                variant="link" 
                onClick={() => window.location.href = "/login"} 
                className="text-blue-600 hover:text-blue-800"
              >
                Return to Login
              </Button>
            </div>
          </CardFooter>
        </Card>
        
        {/* Support Information - Hidden as requested */}
        {/* <div className="text-center mt-6 text-sm text-gray-600">
          <p>Need help? Contact support at:</p>
          <a href="mailto:clynoxofficial@gmail.com" className="text-blue-600 hover:text-blue-800">
            clynoxofficial@gmail.com
          </a>
        </div> */}
      </div>
    </div>
  );
}